import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { languagesService, Language } from '@/services/languages';

const SITE_URL = 'https://campervanrentalturkey.com';

// Pages that should NOT have hreflang tags (noindex pages)
const EXCLUDED_PATTERNS = [
  '/admin',
  '/owner',
  '/demo',
  '/login',
  '/dashboard',
  '/cart',
  '/reset-password',
  '/thank-you',
  '/payment-callback',
  '/payment-process',
  '/complete-payment',
];

export const HreflangTags = () => {
  const location = useLocation();
  const [languages, setLanguages] = useState<Language[]>([]);

  useEffect(() => {
    // Defer loading of languages for hreflang tags to avoid blocking LCP
    // Hreflang tags are important for SEO but not for the visual rendering
    // Increased to 8000ms to be absolutely sure it's after LCP
    const timer = setTimeout(() => {
      loadLanguages();
    }, 8000);
    return () => clearTimeout(timer);
  }, []);

  const loadLanguages = async () => {
    try {
      const data = await languagesService.getActive();
      setLanguages(data);
    } catch (error) {
      console.error('Error loading languages:', error);
    }
  };

  // Get current path without trailing slash
  let currentPath = location.pathname.toLowerCase();
  if (currentPath !== '/' && currentPath.endsWith('/')) {
    currentPath = currentPath.slice(0, -1);
  }
  
  // Check if this page should be excluded from hreflang tags
  const shouldExclude = EXCLUDED_PATTERNS.some(pattern => 
    currentPath.startsWith(pattern)
  );

  // Direct DOM manipulation for crawler compatibility
  useEffect(() => {
    if (shouldExclude || languages.length === 0) return;

    // Extract the clean path (without language prefix)
    const pathParts = currentPath.split('/').filter(Boolean);
    const firstPart = pathParts[0];
    const nonEnglishCodes = languages.filter(l => l.code !== 'en').map(l => l.code);
    const hasLangPrefix = nonEnglishCodes.includes(firstPart);
    
    let cleanPath = hasLangPrefix 
      ? '/' + pathParts.slice(1).join('/') 
      : currentPath;
      
    if (!cleanPath.startsWith('/')) cleanPath = '/' + cleanPath;
    cleanPath = cleanPath.replace(/\/index\.(html|php)$/, '/');
    if (cleanPath !== '/' && cleanPath.endsWith('/')) {
      cleanPath = cleanPath.slice(0, -1);
    }

    // Remove existing hreflang links
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());

    // Add hreflang links via DOM
    languages.forEach(lang => {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = lang.code;
      if (lang.code === 'en') {
        link.href = cleanPath === '/' ? SITE_URL : `${SITE_URL}${cleanPath}`;
      } else {
        link.href = cleanPath === '/' ? `${SITE_URL}/${lang.code}` : `${SITE_URL}/${lang.code}${cleanPath}`;
      }
      document.head.appendChild(link);
    });

    // Add x-default
    const xDefaultLink = document.createElement('link');
    xDefaultLink.rel = 'alternate';
    xDefaultLink.hreflang = 'x-default';
    xDefaultLink.href = cleanPath === '/' ? SITE_URL : `${SITE_URL}${cleanPath}`;
    document.head.appendChild(xDefaultLink);

    return () => {
      document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
    };
  }, [currentPath, languages, shouldExclude]);

  if (shouldExclude || languages.length === 0) {
    return null;
  }

  // Extract the clean path for Helmet
  const pathParts = currentPath.split('/').filter(Boolean);
  const firstPart = pathParts[0];
  const nonEnglishCodes = languages.filter(l => l.code !== 'en').map(l => l.code);
  const hasLangPrefix = nonEnglishCodes.includes(firstPart);
  
  let cleanPath = hasLangPrefix 
    ? '/' + pathParts.slice(1).join('/') 
    : currentPath;
    
  if (!cleanPath.startsWith('/')) cleanPath = '/' + cleanPath;
  cleanPath = cleanPath.replace(/\/index\.(html|php)$/, '/');
  if (cleanPath !== '/' && cleanPath.endsWith('/')) {
    cleanPath = cleanPath.slice(0, -1);
  }

  // Build hreflang links
  const hreflangLinks = languages.map(lang => {
    let href = '';
    if (lang.code === 'en') {
      href = cleanPath === '/' ? SITE_URL : `${SITE_URL}${cleanPath}`;
    } else {
      href = cleanPath === '/' ? `${SITE_URL}/${lang.code}` : `${SITE_URL}/${lang.code}${cleanPath}`;
    }
    
    return { hreflang: lang.code, href };
  });

  const xDefaultHref = cleanPath === '/' ? SITE_URL : `${SITE_URL}${cleanPath}`;

  return (
    <Helmet>
      {hreflangLinks.map(link => (
        <link key={link.hreflang} rel="alternate" hrefLang={link.hreflang} href={link.href} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={xDefaultHref} />
    </Helmet>
  );
};
